import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useGetQuizQuestionsQuery,
  useCreateQuizQuestionMutation,
  useGetProductsQuery,
} from './productKnowledgeApiSlice';
import { PAGE_SIZE } from '../../constants';
import CustomDropdown from '../../components/CustomDropdown';
import { Modal, Pill, fieldStyle, labelStyle } from '../../components/adminUi';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import { BandButton, ChipRow, Dash, ListPageShell, ListPagination, ListTable, RowAction, SearchField, SquareButton, Tag, chip, clamp2, pillButton } from '../../components/listPage';

// Where a question comes from (the shared pool vs a trainee's 복습), then what kind it is —
// both as toggles: tap again to clear, nothing selected is everything. 서술형, not 주관식: the
// same word 퀴즈 기록 and the quiz itself use.
const SOURCES = [['공통', 'common'], ['맞춤', 'remedial']];
const TYPES = [['객관식', 'MC'], ['서술형', 'OE']];
const typeLabel = t => (t === 'OE' ? '서술형' : '객관식');

// Drawn with adminUi's Modal, like 파트너 관리's — not Semantic's stock form.
const QuizQuestionModal = ({ open, onClose, productOptions }) => {
  const [createQuestion] = useCreateQuizQuestionMutation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ product: '', source_evaluation: '', question_text: '', question_type: 'MC', answer_meta: '{}' });
  const set = (k, v) => setFormData(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      await createQuestion({ ...formData, answer_meta: JSON.parse(formData.answer_meta) }).unwrap();
      onClose();
    } catch (err) {
      setError(err instanceof SyntaxError ? '정답 메타데이터가 올바른 JSON이 아닙니다.' : '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 퀴즈 문제"
      width={560}
      footer={
        <>
          <Pill variant="ghost" onClick={onClose} disabled={saving}>취소</Pill>
          <Pill variant="primary" onClick={handleSubmit} disabled={saving || !formData.product || !formData.question_text.trim()}>
            {saving ? '저장 중…' : '저장'}
          </Pill>
        </>
      }
    >
      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>관련 제품</label>
        <CustomDropdown searchable placeholder="제품 선택" options={productOptions} value={formData.product} onChange={v => set('product', v)} icon="box" style={{ width: '100%' }} />
      </div>
      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>유형</label>
        <div style={{ display: 'flex', gap: '6px' }}>
          {TYPES.map(([label, value]) => (
            <button key={value} type="button" onClick={() => set('question_type', value)} style={chip(formData.question_type === value)}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>질문</label>
        <textarea style={{ ...fieldStyle, minHeight: '96px', resize: 'vertical' }} value={formData.question_text} onChange={e => set('question_text', e.target.value)} />
      </div>
      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>소스 평가 ID</label>
        <input style={fieldStyle} value={formData.source_evaluation} onChange={e => set('source_evaluation', e.target.value)} />
      </div>
      <div>
        <label style={labelStyle}>정답 메타데이터 (JSON)</label>
        <textarea style={{ ...fieldStyle, minHeight: '110px', resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '13px' }} value={formData.answer_meta} onChange={e => set('answer_meta', e.target.value)} />
      </div>
      {error && <div style={{ marginTop: '10px', color: '#D93025', fontSize: '13px' }}>{error}</div>}
    </Modal>
  );
};

const QuizQuestionManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const queryParams = new URLSearchParams(location.search);
  const get = k => queryParams.get(k);
  const page = Number(get('page') || 1);
  const page_size = Number(get('page_size') || PAGE_SIZE);
  const ordering = get('ordering');
  const [searchValue, setSearchValue] = useState(get('search') || '');
  const [filtersOpen, setFiltersOpen] = useState(!!get('product'));
  const [isModalOpen, setIsModalOpen] = useState(false);

  const qp = Object.fromEntries(
    Object.entries({ page, page_size, search: get('search'), ordering, product: get('product'), question_type: get('question_type'), source: get('source') }).filter(([, v]) => v),
  );
  const { data: questionsData, isLoading } = useGetQuizQuestionsQuery(qp);
  const { data: productsData } = useGetProductsQuery({ page_size: 1000 });
  const products = productsData?.results || [];
  const productOptions = products.map(p => ({ key: p.id, value: p.id, text: p.name }));
  const productName = q => products.find(p => p.id === q.product)?.name;

  const setQuery = updates => {
    const p = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!updates.page) p.delete('page');
    navigate(`${location.pathname}?${p.toString()}`);
  };
  const sortBy = f => setQuery({ ordering: ordering === `-${f}` ? f : `-${f}` });
  const search = () => setQuery({ search: searchValue.trim() });

  const productDropdown = (
    <CustomDropdown
      searchable
      placeholder="제품 선택"
      options={productOptions}
      value={get('product')}
      onChange={v => setQuery({ product: v })}
      icon="box"
      style={isMobile ? { width: '100%' } : { minWidth: '180px', flex: '0 0 auto' }}
    />
  );
  const searchField = <SearchField isMobile={isMobile} value={searchValue} onChange={setSearchValue} onSubmit={search} placeholder="질문 검색" />;
  const sourceChips = SOURCES.map(([label, value]) => {
    const active = get('source') === value;
    return <button key={label} type="button" onClick={() => setQuery({ source: active ? null : value })} style={chip(active)}>{label}</button>;
  });
  const typeChips = TYPES.map(([label, value]) => {
    const active = get('question_type') === value;
    return <button key={label} type="button" onClick={() => setQuery({ question_type: active ? null : value })} style={chip(active)}>{label}</button>;
  });
  // A remedial question belongs to one trainee; the general pool has none.
  const target = q => q.user_display_name || q.user_username;

  return (
    <ListPageShell
      isMobile={isMobile}
      icon="question circle"
      title="퀴즈 문제 관리"
      subtitle="퀴즈 문제와 정답, 맞춤 복습 문제"
      actions={<BandButton primary compact={isMobile} icon="plus" onClick={() => setIsModalOpen(true)}>문제 추가</BandButton>}
    >
      {isMobile ? (
        <>
          <div style={{ display: 'flex', gap: '8px' }}>
            {searchField}
            <SquareButton icon="sliders horizontal" label="필터" on={filtersOpen} set={!!get('product')} onClick={() => setFiltersOpen(o => !o)} />
          </div>
          {filtersOpen && <div style={{ marginTop: '8px' }}>{productDropdown}</div>}
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {productDropdown}
          {searchField}
          <button type="button" onClick={search} style={pillButton(true)}>검색</button>
        </div>
      )}
      {/* One row at every width: 공통 맞춤 | 객관식 서술형, all toggles. */}
      <ChipRow count={questionsData?.count} groups={[sourceChips, typeChips]} />
      <ListTable
        isMobile={isMobile}
        loading={isLoading}
        rows={questionsData?.results || []}
        ordering={ordering}
        onSort={sortBy}
        empty="검색 결과가 없습니다."
        onRowClick={q => navigate(`/admin/quiz-questions/${q.id}`)}
        columns={[
          { key: 'created', label: '생성일', sort: 'created_at', muted: true, render: q => (q.created_at ? shortDate(q.created_at) : <Dash />) },
          { key: 'product', label: '제품', sort: 'product', nowrap: true, render: q => <span style={{ fontWeight: 600 }}>{productName(q) || <Dash />}</span> },
          { key: 'question', label: '질문', sort: 'question_text', render: q => <div style={{ ...clamp2, color: '#424245' }}>{q.question_text}</div> },
          { key: 'type', label: '유형', sort: 'question_type', render: q => <Tag tone="gray">{typeLabel(q.question_type)}</Tag> },
          {
            key: 'target', label: '대상', nowrap: true,
            render: q => (target(q) ? (
              <>
                <div style={{ fontWeight: 600 }}>{target(q)}</div>
                {q.user_contractor && <div style={{ fontSize: '12px', color: '#86868b' }}>{q.user_contractor}</div>}
              </>
            ) : <span style={{ color: '#86868b' }}>공통</span>),
          },
          {
            key: 'session', label: '', align: 'right',
            render: q => q.chat_session_id && <RowAction icon="comments outline" title="출처 롤플레이" onClick={() => navigate(`/chatsessions/${q.chat_session_id}`)} />,
          },
        ]}
        card={{
          title: q => productName(q) || '퀴즈',
          tags: q => [<Tag key="t" tone="gray">{typeLabel(q.question_type)}</Tag>],
          meta: q => [q.created_at && shortDate(q.created_at), target(q) ? `맞춤 · ${target(q)}` : '공통'],
          body: q => q.question_text,
        }}
      />
      <ListPagination page={page} count={questionsData?.count || 0} pageSize={page_size} onChange={pg => setQuery({ page: String(pg) })} />
      {isModalOpen && <QuizQuestionModal open={isModalOpen} onClose={() => setIsModalOpen(false)} productOptions={productOptions} />}
    </ListPageShell>
  );
};

export default QuizQuestionManagement;
